import { useState, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface AccordionItem {
  id: string;
  title: ReactNode;
  content: ReactNode;
  defaultOpen?: boolean;
  icon?: ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /** Pozwól otwarte kilka itemów naraz. Default: false (tylko jeden). */
  allowMultiple?: boolean;
  className?: string;
}

/**
 * Expand/collapse lista sekcji. Default: tylko jeden otwarty naraz
 * (pattern z FAQ, settings grup).
 */
export function Accordion({ items, allowMultiple = false, className }: AccordionProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const init = new Set<string>();
    items.forEach(item => { if (item.defaultOpen) init.add(item.id); });
    return init;
  });

  const toggle = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={['rounded-xl border border-border-subtle bg-surface-raised/30 overflow-hidden divide-y divide-border-subtle', className].filter(Boolean).join(' ')}>
      {items.map(item => {
        const isOpen = openIds.has(item.id);
        return (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => toggle(item.id)}
              aria-expanded={isOpen}
              aria-controls={`accordion-${item.id}`}
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-hover/40 transition-colors focus:outline-none focus-visible:bg-surface-hover/40"
            >
              {item.icon && (
                <span className="inline-flex items-center justify-center w-5 h-5 shrink-0 text-text-muted [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span className="flex-1 text-sm font-medium text-text-primary">{item.title}</span>
              <ChevronDown
                className={`w-4 h-4 text-text-muted shrink-0 transition-transform duration-fast ${isOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <div
                id={`accordion-${item.id}`}
                role="region"
                className="px-5 pb-5 pt-2 text-sm text-text-secondary animate-slide-down"
              >
                {item.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
